import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Printer, QrCode, UtensilsCrossed } from "lucide-react";
import { AppDispatch } from "../../../../../dataManagement/uiDb/UiDb";
import {
  openBoardForBusiness,
  selectDineInBoardSessions,
  selectDineInBoardProducts,
  selectDineInBoardLoadingMap,
  selectDineInBoardErrorMap,
} from "../../../../../dataManagement/uiDb/DineInBoardDb";
import DineInBoardThunk from "../../../../../services/thunks/DineInBoardThunk";
import { useLiveSync } from "../../../../../hooks/useLiveSync";
import {
  buildDineInBoard,
  buildDineInBoardStats,
  DEFAULT_DINE_IN_TABLE_COUNT,
} from "./dinein/dineInBoardBuilder";
import DineInBoardStats from "./dinein/DineInBoardStats";
import DineInBoardLegend from "./dinein/DineInBoardLegend";
import DineInBoardToggle, { DineInBoardView } from "./dinein/DineInBoardToggle";
import DineInTableGrid from "./dinein/DineInTableGrid";
import DineInTableDetail from "./dinein/DineInTableDetail";
import DineInQrGrid from "./dinein/DineInQrGrid";
import DineInQrModal from "./dinein/DineInQrModal";
import DineInPrintSheet from "./dinein/DineInPrintSheet";
import "./styles/DineInTab.css";

interface DineInTabProps {
  businessId: string;
  /** Printed on every QR card so a customer knows who they're ordering from. */
  businessName: string;
  /** Shown beside the name on QR cards and tents. Nine's logo sits inside the
   *  code itself, so the restaurant's goes around it — see DineInQrCard. */
  businessLogoUrl?: string;
  /** From businessConfig.dineInTableCount — see its TODO, there's no save path yet. */
  tableCount?: number;
}

/** How often the "seated 27m" labels re-render. */
const CLOCK_TICK_MS = 30_000;

/**
 * Staff-facing dine-in floor for one business — the other half of the diner's
 * table screen. Owns fetching, the shared clock, and the grid/detail switch;
 * every derivation lives in dineInBoardBuilder, and every number it shows
 * comes from DineInSummaryHelper, so this file stays about orchestration.
 *
 * Which table is open is held in the URL (`?table=4`) rather than component
 * state, matching OrdersTab's `?orders=history`. That makes a table
 * deep-linkable and bookmarkable, keeps the back button working, and means a
 * counter tablet reloading mid-service returns to the table it was on instead
 * of the top of the floor.
 */
const DineInTab: React.FC<DineInTabProps> = ({
  businessId,
  businessName,
  businessLogoUrl,
  tableCount,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  // Local rather than URL state, unlike ?table=: this is a view preference on
  // one screen, not a destination worth linking to or pressing back out of.
  const [view, setView] = useState<DineInBoardView>("tables");
  const [qrTable, setQrTable] = useState<number | null>(null);
  const [printing, setPrinting] = useState(false);

  const sessions = useSelector(selectDineInBoardSessions);
  const products = useSelector(selectDineInBoardProducts);
  const loadingMap = useSelector(selectDineInBoardLoadingMap);
  const errorMap = useSelector(selectDineInBoardErrorMap);
  const isLoading = !!loadingMap.fetchSessions;
  const error = errorMap.fetchSessions;

  const [now, setNow] = useState(() => Date.now());

  const effectiveTableCount =
    typeof tableCount === "number" && tableCount > 0
      ? tableCount
      : DEFAULT_DINE_IN_TABLE_COUNT;

  // Claim the slice for this business BEFORE fetching, so another business's
  // tables can't flash under this one's name while the request is in flight.
  useEffect(() => {
    if (!businessId) return;
    dispatch(openBoardForBusiness(businessId));
    dispatch(DineInBoardThunk.fetchDineInBoardSessions({ businessId }));
  }, [dispatch, businessId]);

  // Resolve name/price for every product referenced across the floor. Re-runs
  // as new tables order things we haven't looked up yet; the slice merges
  // rather than replaces, so nothing already priced gets dropped.
  useEffect(() => {
    const knownIds = new Set(products.map((p) => p.productId));
    const missingIds = Array.from(
      new Set(sessions.flatMap((s) => s.roundItems.map((i) => i.productId)))
    ).filter((id) => id && !knownIds.has(id));
    if (missingIds.length > 0) {
      dispatch(DineInBoardThunk.fetchDineInBoardProducts(missingIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  // Silent refetch whenever staff come back to the screen. This is the only
  // liveness the board has until business-scoped pushes exist, and it's the
  // reason refreshDineInBoardSessions never touches loadingMap: coming back to
  // a floor of live tables must not blank them into skeletons.
  useLiveSync(!!businessId, () => {
    dispatch(DineInBoardThunk.refreshDineInBoardSessions({ businessId }));
  });

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const tiles = useMemo(
    () => buildDineInBoard(effectiveTableCount, sessions, products),
    [effectiveTableCount, sessions, products]
  );
  const stats = useMemo(() => buildDineInBoardStats(tiles), [tiles]);

  const tableParam = searchParams.get("table");
  const selectedTile =
    tableParam !== null
      ? (tiles.find((t) => t.tableNumber === Number(tableParam)) ?? null)
      : null;

  const openTable = (tableNumber: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("table", String(tableNumber));
    // Pin the tab in the URL too. Which tab is active is otherwise local state
    // in BusinessListings, so without this a reload on ?table=4 would restore
    // the table but land on Products — the deep link would only half work.
    // Safe to set after mount: the page's scroll-past-the-banner effect
    // latches on first commit, so adding ?tab= later can't retrigger it.
    next.set("tab", "dinein");
    setSearchParams(next, { replace: false });
  };

  const closeTable = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("table");
    setSearchParams(next, { replace: false });
  };

  const retry = () => dispatch(DineInBoardThunk.fetchDineInBoardSessions({ businessId }));

  // The board is still loading and has nothing to show yet. Guarded because
  // buildDineInBoard always returns a full set of tiles — so a deep link to
  // ?table=4 would otherwise open the ticket against a placeholder and flash
  // "nobody is seated" before the real session lands.
  const isFirstLoad = isLoading && sessions.length === 0;

  if (error && sessions.length === 0) {
    return (
      <div className="dine-in-tab__error">
        <AlertCircle size={32} color="#f87171" />
        <p>{error}</p>
        <button className="dine-in-tab__retry" onClick={retry}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="dine-in-tab">
      {isFirstLoad ? (
        <div className="dine-in-tab__skeletons">
          {Array.from({ length: effectiveTableCount }).map((_, i) => (
            <div key={i} className="dine-in-tab__skeleton" />
          ))}
        </div>
      ) : (
        <>
          <DineInBoardToggle view={view} onChange={setView} />

          {view === "tables" ? (
            <>
              <DineInBoardStats stats={stats} />
              <DineInBoardLegend />
              {stats.activeNow === 0 && (
                <p className="dine-in-tab__quiet">
                  <UtensilsCrossed size={14} strokeWidth={2} />
                  No tables are ordering right now.
                </p>
              )}
              <DineInTableGrid tiles={tiles} now={now} onOpenTable={openTable} />
            </>
          ) : (
            <>
              <div className="dine-in-tab__qr-bar">
                <p className="dine-in-tab__quiet dine-in-tab__quiet--inline">
                  <QrCode size={14} strokeWidth={2} />
                  Each code opens that table's ordering page. Safe to print — no
                  session is baked in.
                </p>
                <button
                  type="button"
                  className="dine-in-tab__print-btn"
                  onClick={() => setPrinting(true)}
                >
                  <Printer size={15} strokeWidth={2} />
                  Print table tents
                </button>
              </div>
              <DineInQrGrid
                businessId={businessId}
                businessName={businessName}
                businessLogoUrl={businessLogoUrl}
                tableNumbers={tiles.map((t) => t.tableNumber)}
                onOpenQr={setQrTable}
              />
            </>
          )}
        </>
      )}

      {qrTable !== null && (
        <DineInQrModal
          businessId={businessId}
          businessName={businessName}
          businessLogoUrl={businessLogoUrl}
          tableNumber={qrTable}
          onClose={() => setQrTable(null)}
        />
      )}

      {printing && (
        <DineInPrintSheet
          businessId={businessId}
          businessName={businessName}
          businessLogoUrl={businessLogoUrl}
          tableNumbers={tiles.map((t) => t.tableNumber)}
          onDone={() => setPrinting(false)}
        />
      )}

      {/* Rendered ALONGSIDE the grid, not instead of it. The ticket portals
          itself out to a full-screen layer, so leaving the board mounted costs
          nothing visually and keeps its scroll position, its clock and its
          focus-resync alive — backing out lands on a live, unmoved floor
          rather than a reloaded one. */}
      {selectedTile && !isFirstLoad && (
        <DineInTableDetail
          tile={selectedTile}
          businessId={businessId}
          businessName={businessName}
          businessLogoUrl={businessLogoUrl}
          products={products}
          now={now}
          onBack={closeTable}
        />
      )}
    </div>
  );
};

export default DineInTab;
